/**
 * CSV serialisation (FR-RPT-10).
 *
 * Hand-rolled, as AD-06 decided: CSV is quoting and escaping, and a dependency for forty
 * lines of it fails "justify every dependency".
 *
 * Pure, so the escaping rules are testable without a browser. The rules are RFC 4180:
 * a field containing a comma, a quote or a newline is wrapped in quotes, and an embedded
 * quote is doubled.
 *
 * Two decisions worth stating:
 *
 *  · **Money is written as a plain decimal number**, not "₹1,234.56". A currency symbol
 *    and thousands separators make the column text in every spreadsheet that opens it,
 *    which is the CSV equivalent of the failure FR-RPT-11 forbids in Excel.
 *  · **Dates are written ISO**, `YYYY-MM-DD`. It is unambiguous, it sorts, and it is the
 *    one format that does not depend on the reader's locale — which matters here, because
 *    the source workbook's `mm-dd-yy` in an Indian context is defect 6.
 */

import type { Report, ReportColumn } from './reports';

const NEEDS_QUOTING = /[",\r\n]/;

export function escapeCsvField(value: string): string {
  if (!NEEDS_QUOTING.test(value)) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * A cell as CSV text.
 *
 * `null` becomes empty rather than "null" — an absent count in a rollup-sourced report
 * means "not stored", and printing the word would be a lie about the data.
 */
export function csvCell(value: string | number | null, column: ReportColumn): string {
  if (value === null || value === undefined) return '';

  switch (column.type) {
    case 'money':
      // Paise to rupees, two decimals, no symbol and no grouping.
      return (Number(value) / 100).toFixed(2);
    case 'percent':
      // Basis points to a percentage, so 1234 reads 12.34.
      return (Number(value) / 100).toFixed(2);
    case 'number':
      return String(value);
    case 'date':
    case 'text':
      return escapeCsvField(String(value));
  }
}

export function toCsv(report: Report, columns: ReportColumn[] = report.columns): string {
  const lines: string[] = [];

  lines.push(columns.map((c) => escapeCsvField(c.label)).join(','));

  for (const row of report.rows) {
    lines.push(columns.map((c) => csvCell(row.cells[c.key] ?? null, c)).join(','));
  }

  if (report.totals) {
    lines.push(columns.map((c) => csvCell(report.totals!.cells[c.key] ?? null, c)).join(','));
  }

  // CRLF per RFC 4180. Excel on Windows is the common consumer and handles LF too, but
  // there is no cost to being correct.
  return `${lines.join('\r\n')}\r\n`;
}
